import React, { useEffect, useState } from "react";
import API_URL from "../api/API";

const { FRONTEND_GET_USER_RESPONSE_PAGE_DATA, FRONTEND_UPDATE_USER_RESPONSE } = API_URL;

const ResponsePage = () => {
  const query = new URLSearchParams(window.location.search);
  const key = query.get("key");
  const workoutKey = query.get("workoutKey");
  const index = query.get("index");

  const [loading, setLoading] = useState(true);
  const [stepData, setStepData] = useState(null);
  const [captureValue, setCaptureValue] = useState("");
  const [selectedResponse, setSelectedResponse] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchStepData = async () => {
    setLoading(true);
    setStatusMessage("");
    try {
      const res = await fetch(
        `${FRONTEND_GET_USER_RESPONSE_PAGE_DATA}?key=${encodeURIComponent(
          key
        )}&workoutKey=${encodeURIComponent(workoutKey)}&index=${encodeURIComponent(index)}`
      );
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Failed to load step data.");
      }
      const data = await res.json();
      setStepData(data);
      setCaptureValue(data.step?.captureValue || "");
      setSelectedResponse(data.step?.status || "");
      if (["completed", "slipped", "skipped"].includes(data.step?.status)) {
        setStatusMessage(data.step?.nudgeMessage || `✅ Step already done: ${data.step.status}`);
      }
    } catch (err) {
      console.error(err);
      setStatusMessage("❌ " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!key || !workoutKey || !index) {
      setStatusMessage("⚠️ Missing query parameters.");
      setLoading(false);
      return;
    }
    fetchStepData();
  }, [key, workoutKey, index]);

  const handleSubmit = async () => {
    if (!selectedResponse) {
      setStatusMessage("⚠️ Please select a response before submitting.");
      return;
    }
    setSubmitting(true);
    setStatusMessage("");
    try {
      const res = await fetch(
        `${FRONTEND_UPDATE_USER_RESPONSE}?key=${encodeURIComponent(
          key
        )}&workoutKey=${encodeURIComponent(workoutKey)}&index=${encodeURIComponent(index)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ responseValue: selectedResponse, captureValue }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to submit response.");

      setStepData(prev => ({
        ...prev,
        step: data.step,
        enrollment: { ...prev.enrollment, completed: data.enrollmentCompleted },
      }));

      const nudgeBtn = stepData.workout.responseButtons.find(btn => btn.value === selectedResponse);
      setStatusMessage(nudgeBtn?.nudgeMessage || data.message);
    } catch (err) {
      console.error(err);
      setStatusMessage("❌ " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen text-gray-700">Loading...</div>;
  if (!stepData) return <div className="flex justify-center items-center h-screen text-gray-700">{statusMessage || "No data"}</div>;

  const step = stepData.step;
  const workout = stepData.workout;
  const isCompleted = ["completed", "slipped", "skipped"].includes(step.status);

  const btnColors = { completed: "#28a745", slipped: "#ffc107", skipped: "#dc3545" };

  return (
    <div className="min-h-screen bg-gray-50 p-4 flex flex-col items-center font-sans">
      {/* Brand */}
      <h1 className="text-3xl font-bold text-blue-600 mb-6">SelfReformBot</h1>

      {/* Workout Card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800">{workout.title}</h2>
        <p className="text-gray-500 italic mb-4">{workout.topic}</p>

        {/* Capture Section */}
        {workout.capture?.enabled && (
          <div className="mb-4">
            <h4 className="font-medium text-gray-700">{workout.capture.promptTitle}</h4>
            <p className="text-gray-500 mb-2">{workout.capture.promptText}</p>
            {workout.capture.inputType === "text" ? (
              <textarea
                rows={4}
                value={captureValue}
                onChange={e => setCaptureValue(e.target.value)}
                disabled={isCompleted}
                className="w-full p-3 border rounded-lg border-gray-300 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              />
            ) : (
              <div className="flex flex-col gap-2">
                {workout.capture.choices?.map(choice => (
                  <label key={choice} className={`flex items-center gap-2 cursor-pointer ${isCompleted ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <input
                      type="radio"
                      name="captureChoice"
                      value={choice}
                      disabled={isCompleted}
                      checked={captureValue === choice}
                      onChange={() => setCaptureValue(choice)}
                      className="accent-blue-500"
                    />
                    <span className="text-gray-700">{choice}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Response Buttons */}
        <div className="flex flex-col gap-3 mb-4">
          {workout.responseButtons.map(btn => (
            <button
              key={btn.value}
              onClick={() => !isCompleted && setSelectedResponse(btn.value)}
              disabled={isCompleted || submitting}
              style={{ background: selectedResponse === btn.value ? btnColors[btn.value] : "#f0f0f0" }}
              className={`w-full py-3 rounded-lg font-medium transition-colors duration-200 ${
                selectedResponse === btn.value ? "text-white" : "text-gray-800"
              } hover:brightness-95`}
            >
              {btn.label}
            </button>
          ))}
        </div>

        {/* Submit */}
        {!isCompleted && (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
          >
            {submitting ? "Submitting..." : "Submit Answer"}
          </button>
        )}

        {/* Status Message */}
        {statusMessage && (
          <p
            className={`mt-4 font-semibold ${
              statusMessage.startsWith("❌") ? "text-red-500" : "text-green-600"
            }`}
          >
            {statusMessage}
          </p>
        )}

        {/* Already Completed */}
        {isCompleted && <p className="mt-2 text-gray-500 italic">Step already marked as "{step.status}"</p>}
      </div>
    </div>
  );
};

export default ResponsePage;
